import { useAuth } from "@clerk/react";
import { Link, type LinkProps, useRouterState } from "@tanstack/react-router";
import { MenuIcon } from "lucide-react";
import * as React from "react";
import { Suspense, useEffect, useState } from "react";

import { WithcoWordmark } from "@/web/assets";
import { AuthnFormDialog } from "@/web/auth/AuthnFormDialog";
import { SignInButton } from "@/web/auth/SignInButton";
import { useUser } from "@/web/auth/useUser";
import { Button } from "@/web/components/primitives/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/web/components/primitives/dropdown-menu";
import { Spinner } from "@/web/components/primitives/spinner";
import {
  AccountMenuLinkItems,
  SignedInAccountMenu,
  SignOutMenuItem,
} from "@/web/components/styled/AccountMenu";
import { cn } from "@/web/lib/styling";

// =============================================================================
// Types
// =============================================================================

export type NavItemProps = {
  label: string;
  to: LinkProps["to"];
  /** Hide this link from signed-out visitors. Applied centrally in the provider
   *  so the desktop nav and the mobile menu can never disagree about it. */
  authedOnly?: boolean;
};

/**
 * Primary marketing/app routes shown in desktop nav and the mobile menu. Empty
 * for now: the nav's actions are the CTAs on the right, and account
 * destinations (Portfolio, Business Dashboard) live in the account menu.
 */
export const PRIMARY_NAV_LINKS: NavItemProps[] = [];

export const DEFAULT_APP_NAV_LINKS = PRIMARY_NAV_LINKS;

export type NavbarPosition = "static" | "sticky" | "fixed";

interface NavbarContextValue {
  state: {
    position: NavbarPosition;
    shouldBlur: boolean;
    responsive: boolean;
    mobileMenuClassName: string;
  };
  meta: {
    navLinks: NavItemProps[];
    /** Extra rows injected at the top of the mobile hamburger menu (e.g. the
     * desktop CTAs). Rendered inside their own `DropdownMenuGroup` followed by a
     * divider, so pass bare `DropdownMenuItem`s. Desktop callers place their own
     * buttons in `Right`. */
    mobileMenuExtras?: React.ReactNode;
  };
}

// =============================================================================
// Context
// =============================================================================

const NavbarContext = React.createContext<NavbarContextValue | null>(null);

function useNavbarContext() {
  const context = React.use(NavbarContext);
  if (!context) {
    throw new Error("AppNavbar subcomponents must be used within AppNavbar.Provider");
  }
  return context;
}

// =============================================================================
// Provider
// =============================================================================

interface NavbarProviderProps {
  children: React.ReactNode;
  navLinks?: NavItemProps[];
  position?: NavbarPosition;
  blur?: boolean;
  responsive?: boolean;
  mobileMenuClassName?: string;
  /** Extra rows for the top of the mobile hamburger menu (pass `DropdownMenuItem`s). */
  mobileMenuExtras?: React.ReactNode;
}

function NavbarProvider({
  children,
  navLinks = DEFAULT_APP_NAV_LINKS,
  position = "sticky",
  blur = false,
  responsive = true,
  mobileMenuClassName = "text-withus-dark-blue",
  mobileMenuExtras,
}: NavbarProviderProps) {
  const [shouldBlur, setShouldBlur] = useState(false);
  useEffect(() => {
    const id = window.setTimeout(() => setShouldBlur(blur), 10);
    return () => window.clearTimeout(id);
  }, [blur]);

  // Auth-gate the links HERE rather than in `NavLinks` and `MobileMenu`
  // separately: both read `meta.navLinks`, so one filter covers the desktop nav
  // and the hamburger at once, and every caller that passes its own `navLinks`
  // (marketing, community theater) gets the same treatment for free.
  //
  // `isLoaded` false counts as signed-OUT, matching how the menus below already
  // gate their account rows. That way an authed-only link fades in once auth
  // resolves instead of flashing for every visitor on first paint — including
  // during SSR, where no caller is guaranteed to wrap this in `ClientOnly`.
  const { isLoaded, isSignedIn } = useAuth();
  const isAuthed = isLoaded && Boolean(isSignedIn);
  const visibleNavLinks = React.useMemo(
    () => navLinks.filter((item) => !item.authedOnly || isAuthed),
    [navLinks, isAuthed],
  );

  const value = React.useMemo<NavbarContextValue>(
    () => ({
      state: {
        position,
        shouldBlur,
        responsive,
        mobileMenuClassName,
      },
      meta: {
        navLinks: visibleNavLinks,
        mobileMenuExtras,
      },
    }),
    [position, shouldBlur, responsive, mobileMenuClassName, visibleNavLinks, mobileMenuExtras],
  );

  return <NavbarContext value={value}>{children}</NavbarContext>;
}

// =============================================================================
// Frame
// =============================================================================

interface FrameProps extends React.HTMLAttributes<HTMLElement> {
  children: React.ReactNode;
}

function Frame({ children, className, ...props }: FrameProps) {
  const {
    state: { position, shouldBlur },
  } = useNavbarContext();

  const positionClasses = {
    static: "",
    sticky: "sticky top-0",
    fixed: "fixed top-0 inset-x-0",
  };

  return (
    <header
      className={cn(
        "z-40 grid w-full grid-cols-3 items-center px-4 py-4 text-foreground md:px-8",
        positionClasses[position],
        shouldBlur && "backdrop-blur-sm",
        className,
      )}
      {...props}
    >
      {children}
    </header>
  );
}

// =============================================================================
// Logo
// =============================================================================

interface LogoProps {
  className?: string;
  homeTo?: LinkProps["to"];
}

function Logo({ className, homeTo = "/" }: LogoProps) {
  const markClass =
    "block h-7 w-auto shrink-0 text-withus-blue transition-colors hover:text-withus-blue/80 md:hidden";
  const wordmarkClass =
    "hidden h-8 w-auto shrink-0 text-withus-blue transition-colors hover:text-withus-blue/80 md:block";

  return (
    <Link to={homeTo} className={cn("shrink-0", className)}>
      <WithcoWordmark aria-hidden className={markClass} />
      <WithcoWordmark aria-hidden className={wordmarkClass} />
    </Link>
  );
}

// =============================================================================
// Nav links
// =============================================================================

function navLinkClasses(isActive: boolean): string {
  return cn(
    "font-withus-sans rounded-none bg-transparent px-2.5 py-2 text-base transition-colors",
    isActive
      ? "font-medium text-foreground"
      : "font-medium text-foreground opacity-50 hover:opacity-80",
  );
}

function normalizePath(path: string): string {
  if (path.length > 1 && path.endsWith("/")) return path.slice(0, -1);
  return path;
}

function hrefFromTo(to: LinkProps["to"]): string {
  if (typeof to === "string") return to;
  if (typeof to === "object" && to !== null && "pathname" in to) {
    return String((to as { pathname: string }).pathname);
  }
  return String(to);
}

function isNavActive(to: LinkProps["to"], pathname: string): boolean {
  return normalizePath(hrefFromTo(to)) === normalizePath(pathname);
}

type NavLinksProps = {
  className?: string;
  /** Override per-link classes. Defaults to standard app nav styling. */
  getLinkClassName?: (isActive: boolean) => string;
};

function NavLinks({ className, getLinkClassName }: NavLinksProps = {}) {
  const {
    state: { responsive },
    meta: { navLinks },
  } = useNavbarContext();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // No links (or all authed-only while signed out): skip the empty landmark.
  if (navLinks.length === 0) return null;

  return (
    <nav
      className={cn("items-center gap-3", responsive ? "hidden lg:flex" : "flex", className)}
      aria-label="Main"
    >
      {navLinks.map((item) => {
        const active = isNavActive(item.to, pathname);
        return (
          <Link
            key={String(item.to)}
            to={item.to}
            className={getLinkClassName?.(active) ?? navLinkClasses(active)}
          >
            {item.label}
          </Link>
        );
      })}
    </nav>
  );
}

// =============================================================================
// Slots
// =============================================================================

interface LeftProps {
  children?: React.ReactNode;
}

function Left({ children }: LeftProps) {
  return (
    <div className="col-start-1 flex min-w-0 flex-row items-center gap-4">
      {children ?? <Logo />}
    </div>
  );
}

interface CenterProps {
  children: React.ReactNode;
}

function Center({ children }: CenterProps) {
  return (
    <div className="col-start-2 flex min-w-0 flex-1 items-center justify-center">{children}</div>
  );
}

interface RightProps {
  children?: React.ReactNode;
}

function Right({ children }: RightProps) {
  const {
    state: { responsive },
  } = useNavbarContext();

  return (
    <div className="col-start-3 flex min-w-0 justify-end">
      <div className={cn("flex-row items-center gap-3", responsive ? "hidden lg:flex" : "flex")}>
        {children ?? <AuthActions />}
      </div>
      {responsive ? <MobileMenu className="lg:hidden" /> : null}
    </div>
  );
}

// =============================================================================
// Auth
// =============================================================================

type AuthActionsProps = {
  signInButtonProps?: React.ComponentProps<typeof SignInButton>;
};
function AuthActions({ signInButtonProps }: AuthActionsProps = {}) {
  const { isLoaded, isSignedIn } = useAuth();
  const { className: signInClassName, ...restSignInButtonProps } = signInButtonProps ?? {};

  if (!isLoaded) {
    return <div className="h-10 w-24 shrink-0 rounded-none bg-muted" aria-hidden />;
  }

  if (!isSignedIn) {
    return (
      <SignInButton
        variant="solid"
        color="darkBlue"
        size="md"
        label="Sign in"
        {...restSignInButtonProps}
        className={cn("font-withus-sans rounded-none", signInClassName)}
      />
    );
  }

  return (
    <Suspense
      fallback={
        <div className="flex size-10 shrink-0 items-center justify-center" aria-hidden>
          <Spinner size="small" />
        </div>
      }
    >
      <SignedInAccountMenu />
    </Suspense>
  );
}

// =============================================================================
// Mobile menu
// =============================================================================

interface MobileMenuProps {
  className?: string;
}

function MobileMenu({ className }: MobileMenuProps) {
  const { isLoaded, isSignedIn } = useAuth();
  const {
    state: { mobileMenuClassName },
    meta: { navLinks, mobileMenuExtras },
  } = useNavbarContext();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [authOpen, setAuthOpen] = useState(false);
  const user = useUser();

  return (
    <div className={className}>
      <AuthnFormDialog
        open={authOpen}
        onOpenChange={setAuthOpen}
        showCloseButton
        title="Welcome"
        description="Continue with Google or email"
      />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Open menu"
            className={cn(
              mobileMenuClassName,
              "size-8 border-current [--button-border-width:1.5px]",
            )}
          >
            <MenuIcon className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {mobileMenuExtras ? (
            <>
              <DropdownMenuGroup>{mobileMenuExtras}</DropdownMenuGroup>
              <DropdownMenuSeparator />
            </>
          ) : null}
          {isLoaded && isSignedIn && (
            <>
              <DropdownMenuLabel className="truncate font-normal text-muted-foreground">
                {user?.email ?? "Account"}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
            </>
          )}
          {/* Signed out, every nav link may be authed-only, leaving this group
              empty — skip it (and its divider) rather than stack two dividers. */}
          {(navLinks.length > 0 || (isLoaded && isSignedIn)) && (
            <>
              <DropdownMenuGroup>
                {navLinks.map((item) => (
                  <DropdownMenuItem key={String(item.to)} asChild>
                    <Link
                      to={item.to}
                      className={cn(
                        "w-full cursor-pointer",
                        isNavActive(item.to, pathname) && "font-semibold",
                      )}
                    >
                      {item.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
                {isLoaded && isSignedIn && <AccountMenuLinkItems />}
              </DropdownMenuGroup>
              <DropdownMenuSeparator />
            </>
          )}
          <DropdownMenuGroup>
            {!isLoaded ? null : !isSignedIn ? (
              <DropdownMenuItem onSelect={() => setAuthOpen(true)}>Sign in</DropdownMenuItem>
            ) : (
              <SignOutMenuItem />
            )}
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

// =============================================================================
// Compound export
// =============================================================================

export const AppNavbar = {
  Provider: NavbarProvider,
  Frame,
  Left,
  Center,
  Right,
  Logo,
  NavLinks,
  AuthActions,
  MobileMenu,
};

export function DefaultNavbar({
  navLinks,
  className,
}: {
  navLinks?: NavItemProps[];
  className?: string;
}) {
  return (
    <NavbarProvider navLinks={navLinks} position="sticky">
      <Frame className={cn("bg-background", className)}>
        <Left>
          <Logo />
        </Left>
        <Center>
          <NavLinks />
        </Center>
        <Right />
      </Frame>
    </NavbarProvider>
  );
}
